// apps/web/src/components/ui/pill.tsx
//
// Phase 6 NEW primitive — multi-select pill used by option-set renderer.
// role="checkbox" semantics (NOT button) so aria-checked + multi-select toggle
// work natively. Visual contract: UI-SPEC §"Pill component spec".
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { cn } from "@/lib/utils";

const pillVariants = cva(
  "inline-flex items-center justify-center rounded-full px-3 py-1 text-sm font-medium border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--recipe-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--recipe-bg)]",
  {
    variants: {
      selected: {
        true: "bg-[var(--recipe-accent)] text-white border-[var(--recipe-accent)]",
        false:
          "bg-transparent text-[var(--recipe-ink)] border-[var(--recipe-line)] hover:ring-2 hover:ring-[var(--recipe-accent-soft)] hover:border-[var(--recipe-accent)]",
      },
    },
    defaultVariants: { selected: false },
  },
);

export interface PillProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof pillVariants> {
  selected: boolean;
  children: React.ReactNode;
}

export const Pill: React.ForwardRefExoticComponent<
  PillProps & React.RefAttributes<HTMLButtonElement>
> = React.forwardRef<HTMLButtonElement, PillProps>(
  ({ className, selected, children, disabled, ...props }, ref) => (
    // <button role="checkbox"> is the documented Phase 6 contract (UI-SPEC
    // §"Pill component spec"). A native <input type="checkbox"> would block the
    // cva-driven selected/unselected pill visual that the design system requires;
    // the button form preserves role + aria-checked + keyboard activation under
    // our own styling.
    // biome-ignore lint/a11y/useSemanticElements: Pill is a custom-styled multi-select primitive; see comment above.
    <button
      ref={ref}
      type="button"
      role="checkbox"
      aria-checked={selected}
      aria-disabled={disabled}
      className={cn(
        pillVariants({ selected }),
        disabled && "opacity-50 pointer-events-none cursor-not-allowed",
        className,
      )}
      disabled={disabled}
      {...props}
    >
      {children}
    </button>
  ),
);
Pill.displayName = "Pill";
